<template>
	<div class="sp-detail">
		<div v-if="intestazione" class="sp-head">
			<img v-if="servizio.logoLargo" :src="servizio.logo" :alt="servizio.nome" height="18" class="mr-1" />
			<v-avatar v-else size="20px" tile class="mr-1">
				<img :src="servizio.logo" :alt="servizio.nome" />
			</v-avatar>
			<span class="sp-head-title">Configurazione {{ servizio.nome }}</span>
			<span class="sp-muted ml-2">{{ modello.active ? 'attivo' : 'disattivato' }}</span>
		</div>

		<div class="sp-body">
			<div v-if="servizio.prossimamente" class="sp-muted sp-small">Disponibile a breve.</div>
			<template v-else>
				<!-- contenuti extra in cima (video, istruzioni) -->
				<slot></slot>
				<div v-if="!campiTesto.length && !campiSwitch.length && !$slots.default" class="sp-muted sp-small">Nessuna impostazione da configurare: basta attivarlo dall'interruttore.</div>
			</template>
			<div v-if="!servizio.prossimamente && campiTesto.length" class="sp-grid">
				<div v-for="c in campiTesto" :key="c.key" :class="{ 'sp-full': c.pieno }">
					<v-textarea v-if="c.tipo === 'textarea'" v-model="modello.setting[c.key]" :label="c.label" :readonly="!!c.readonly" rows="3" :auto-grow="!c.readonly" dense outlined hide-details background-color="white" />
					<!-- combobox: modelli suggeriti ma si può scrivere anche un nome non in elenco -->
					<v-combobox v-else-if="c.tipo === 'combobox'" v-model="modello.setting[c.key]" :items="c.items" :label="c.label" :clearable="!!c.clearable" dense outlined hide-details background-color="white" />
					<v-select v-else-if="c.tipo === 'select'" v-model="modello.setting[c.key]" :items="c.items" :label="c.label" :multiple="!!c.multiple" :chips="!!c.multiple" :small-chips="!!c.multiple" :clearable="!!c.clearable" dense outlined hide-details background-color="white" />
					<v-text-field
						v-else-if="c.tipo === 'password'"
						v-model="modello.setting[c.key]"
						:type="visibili[c.key] ? 'text' : 'password'"
						:append-icon="visibili[c.key] ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
						:label="c.label"
						autocomplete="new-password"
						dense
						outlined
						hide-details
						background-color="white"
						@click:append="$set(visibili, c.key, !visibili[c.key])"
					/>
					<v-text-field v-else-if="c.mask" v-model="modello.setting[c.key]" v-mask="c.mask" :label="c.label" dense outlined hide-details background-color="white" />
					<v-text-field v-else v-model="modello.setting[c.key]" :label="c.label" dense outlined hide-details background-color="white" />
				</div>
			</div>
			<template v-if="!servizio.prossimamente && campiSwitch.length">
				<div class="sp-section" :class="{ 'mt-4': campiTesto.length || $slots.default }">{{ servizio.titoloSwitch || 'Sincronizzazione' }}</div>
				<div class="sp-switches">
					<div v-for="c in campiSwitch" :key="c.key" class="sp-switch">
						<span>{{ c.label }}</span>
						<!-- valore numerico legato all'interruttore (es. dopo quanti secondi), accanto all'interruttore -->
						<v-text-field v-if="c.numero" v-model.number="modello.setting[c.numero.key]" type="number" min="1" :label="c.numero.label" :disabled="!modello.setting[c.key]" class="sp-switch-numero ml-auto" dense outlined hide-details background-color="white" />
						<v-switch v-model="modello.setting[c.key]" color="primary" class="mt-0 pt-0" hide-details></v-switch>
					</div>
				</div>
			</template>
			<!-- contenuti extra dopo campi e interruttori (paesi, template, ...) -->
			<slot v-if="!servizio.prossimamente" name="dopo"></slot>
		</div>

		<div v-if="!servizio.prossimamente" class="sp-foot">
			<!-- bottoni extra a sinistra di Conferma (autorizza, verifica connessione, ...) -->
			<slot name="azioni"></slot>
			<v-btn v-if="conferma" depressed small color="primary" @click="$emit('save')">Conferma</v-btn>
		</div>
	</div>
</template>

<script>
import { VueMaskDirective } from 'v-mask';

// riquadro di configurazione di un servizio (AI / social, CMS, comunicazione, ...): riga espansa (desktop) e card (mobile)
export default {
	name: 'ServicePanel',
	directives: { mask: VueMaskDirective },
	props: {
		// { nome, logo, campi: [{ key, label, tipo?, items?, mask?, pieno? }] }
		servizio: { type: Object, required: true },
		// oggetto del servizio con active e setting: i campi scrivono direttamente in setting
		modello: { type: Object, required: true },
		// nella card mobile logo e nome sono gia' nel titolo
		intestazione: { type: Boolean, default: true },
		// false nasconde Conferma (es. eBay prima dell'autorizzazione)
		conferma: { type: Boolean, default: true },
	},
	data() {
		return {
			// campi password mostrati in chiaro con l'occhio
			visibili: {},
		};
	},
	computed: {
		campiTesto() {
			return (this.servizio.campi || []).filter(c => c.tipo !== 'switch');
		},
		// gli interruttori vanno in una sezione a parte sotto i campi
		campiSwitch() {
			return (this.servizio.campi || []).filter(c => c.tipo === 'switch');
		},
	},
};
</script>

<style scoped>
.sp-detail {
	border: 1px solid rgba(0, 0, 0, 0.12);
	border-radius: 10px;
	overflow: hidden;
	background: #f3f6fa;
	width: 100%;
}
.sp-head {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 4px;
	padding: 8px 12px;
	background: #fff;
	border-bottom: 1px solid #e1e7ef;
	font-size: 13px;
}
.sp-head-title {
	font-weight: 600;
	color: #1f2d3d;
}
.sp-body {
	padding: 12px;
}
.sp-grid {
	display: grid;
	grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
	gap: 12px 16px;
}
.sp-full {
	grid-column: 1 / -1;
}
.sp-section {
	font-size: 11px;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.05em;
	color: #5f6b7a;
	margin-bottom: 6px;
}
.sp-switches {
	display: grid;
	grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
	gap: 8px 16px;
}
.sp-switch-numero {
	flex: 0 0 150px;
}
.sp-switch {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	background: #fff;
	border: 1px solid #e1e7ef;
	border-radius: 8px;
	padding: 8px 12px;
	font-size: 14px;
}
.sp-muted {
	color: #5f6b7a;
}
.sp-small {
	font-size: 13px;
}
.sp-foot {
	display: flex;
	justify-content: flex-end;
	flex-wrap: wrap;
	gap: 8px;
	padding: 8px 12px;
	background: #fff;
	border-top: 1px solid #e1e7ef;
}

@media (max-width: 768px) {
	.sp-body {
		padding: 8px;
	}
	.sp-grid,
	.sp-switches {
		grid-template-columns: 1fr;
	}
}
</style>
